import React, { useEffect, useState, useRef } from "react";
import api, { mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import RichEditor from "@/components/admin/RichEditor";
import { Plus, Pencil, Trash2, X, Upload, Eye, Star, Search } from "lucide-react";

const CATEGORIES = ["News", "Game", "Esports", "Updates", "Industry"];
const EMPTY = { title: "", category: "News", excerpt: "", content: "", cover: "", author: "7 Legendary Staff", tags: [], status: "published", featured: false };

export default function AdminNews() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [q, setQ] = useState("");
  const fileRef = useRef();

  const load = () => api.get("/admin/news").then((r) => setItems(r.data.items)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditing("new"); };
  const openEdit = async (id) => {
    const r = await api.get(`/admin/news/${id}`);
    setForm({ ...r.data, tags: r.data.tags || [] });
    setEditing(id);
  };

  const close = () => { setEditing(null); setForm(EMPTY); };

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      upd("cover", r.data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    const payload = { ...form };
    try {
      if (editing === "new") {
        await api.post("/admin/news", payload);
        toast.success("Article created");
      } else {
        await api.put(`/admin/news/${editing}`, payload);
        toast.success("Article updated");
      }
      close();
      load();
    } catch (err) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this article? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/news/${id}`);
      toast.success("Article deleted");
      load();
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  const filtered = items.filter((a) => !q || a.title.toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">News Management</h1>
          <p className="text-sm text-white/50">Create, edit, and publish articles with images.</p>
        </div>
        <button data-testid="new-article-btn" onClick={openNew} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[hsl(var(--primary))] text-white text-sm font-bold">
          <Plus size={16} /> New Article
        </button>
      </div>

      <div className="relative w-full md:w-80 mb-4">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-news-search" placeholder="Search articles…"
          className="w-full pl-9 pr-3 py-2.5 rounded-lg card-surface text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[hsl(var(--primary))]" />
      </div>

      <div className="card-surface rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-white/40 border-b border-white/10">
              <th className="p-4 font-semibold">Article</th>
              <th className="p-4 font-semibold hidden md:table-cell">Category</th>
              <th className="p-4 font-semibold hidden md:table-cell">Status</th>
              <th className="p-4 font-semibold hidden lg:table-cell">Views</th>
              <th className="p-4 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((a) => (
              <tr key={a.id} data-testid={`news-row-${a.id}`} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02]">
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-14 h-10 rounded-md overflow-hidden bg-white/5 shrink-0">
                      {a.cover && <img src={mediaUrl(a.cover)} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-white truncate max-w-xs flex items-center gap-1.5">
                        {a.featured && <Star size={12} className="fill-current" style={{ color: "hsl(var(--accent))" }} />}
                        {a.title}
                      </div>
                      <div className="text-xs text-white/40">{a.author}</div>
                    </div>
                  </div>
                </td>
                <td className="p-4 hidden md:table-cell text-white/60">{a.category}</td>
                <td className="p-4 hidden md:table-cell">
                  <span className={`px-2 py-0.5 rounded text-xs font-semibold ${a.status === "published" ? "bg-green-500/15 text-green-400" : "bg-amber-500/15 text-amber-400"}`}>
                    {a.status}
                  </span>
                </td>
                <td className="p-4 hidden lg:table-cell text-white/50"><span className="flex items-center gap-1"><Eye size={13} /> {a.views?.toLocaleString() || 0}</span></td>
                <td className="p-4">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => openEdit(a.id)} data-testid={`edit-${a.id}`} className="p-2 rounded-md text-white/50 hover:text-white hover:bg-white/5"><Pencil size={15} /></button>
                    <button onClick={() => remove(a.id)} data-testid={`delete-${a.id}`} className="p-2 rounded-md text-white/50 hover:text-red-400 hover:bg-white/5"><Trash2 size={15} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="p-10 text-center text-white/40">No articles.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/60" onClick={close} />
          <div className="relative w-full max-w-4xl h-full bg-[hsl(var(--card))] border-l border-white/10 overflow-y-auto">
            <div className="sticky top-0 bg-[hsl(var(--card))] border-b border-white/10 px-6 py-4 flex items-center justify-between z-10">
              <h2 className="text-lg font-bold text-white">{editing === "new" ? "New Article" : "Edit Article"}</h2>
              <button onClick={close} className="p-2 rounded-md text-white/50 hover:text-white hover:bg-white/5"><X size={18} /></button>
            </div>
            <div className="p-6 space-y-4">
              <Field label="Title">
                <input data-testid="editor-title" value={form.title} onChange={(e) => upd("title", e.target.value)} className={inputCls} placeholder="Article title" />
              </Field>

              <Field label="Cover Image">
                {form.cover && <img src={mediaUrl(form.cover)} alt="" className="w-full h-40 object-cover rounded-lg mb-3" />}
                <div className="flex gap-2">
                  <input value={form.cover} onChange={(e) => upd("cover", e.target.value)} className={inputCls} placeholder="Paste image URL or upload" />
                  <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="editor-upload"
                    className="shrink-0 inline-flex items-center gap-2 px-3 rounded-lg bg-white/10 text-white text-sm font-semibold hover:bg-white/20">
                    <Upload size={15} /> {uploading ? "…" : "Upload"}
                  </button>
                  <input ref={fileRef} type="file" accept="image/*" hidden onChange={onUpload} />
                </div>
              </Field>

              <div className="grid grid-cols-2 gap-4">
                <Field label="Category">
                  <select data-testid="editor-category" value={form.category} onChange={(e) => upd("category", e.target.value)} className={inputCls}>
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="Status">
                  <select data-testid="editor-status" value={form.status} onChange={(e) => upd("status", e.target.value)} className={inputCls}>
                    <option value="published">Published</option>
                    <option value="draft">Draft</option>
                  </select>
                </Field>
              </div>

              <Field label="Excerpt">
                <textarea data-testid="editor-excerpt" value={form.excerpt} onChange={(e) => upd("excerpt", e.target.value)} rows={2} className={inputCls} placeholder="Short summary" />
              </Field>

              <Field label="Content">
                <RichEditor key={editing} value={form.content} onChange={(v) => upd("content", v)} />
              </Field>

              <Field label="Author">
                <input value={form.author} onChange={(e) => upd("author", e.target.value)} className={inputCls} />
              </Field>

              <Field label="Tags (comma separated)">
                <input value={form.tags.join(", ")} onChange={(e) => upd("tags", e.target.value.split(",").map((t) => t.trim()).filter(Boolean))} className={inputCls} placeholder="valorant, guide" />
              </Field>

              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" data-testid="editor-featured" checked={form.featured} onChange={(e) => upd("featured", e.target.checked)} className="w-4 h-4 accent-[hsl(var(--accent))]" />
                <span className="text-sm text-white/80">Feature on homepage</span>
              </label>
            </div>
            <div className="sticky bottom-0 bg-[hsl(var(--card))] border-t border-white/10 px-6 py-4 flex gap-3">
              <button onClick={close} className="flex-1 py-2.5 rounded-lg border border-white/15 text-white font-semibold hover:bg-white/5">Cancel</button>
              <button onClick={save} disabled={saving} data-testid="editor-save" className="flex-1 py-2.5 rounded-lg bg-[hsl(var(--primary))] text-white font-bold disabled:opacity-50">
                {saving ? "Saving…" : "Save Article"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const inputCls = "w-full px-3 py-2.5 rounded-lg bg-black/30 border border-white/10 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[hsl(var(--primary))]";

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-white/50 mb-1.5">{label}</label>
      {children}
    </div>
  );
}
